import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import {
  EditItAssetDocument,
  GetCurrenciesDocument,
  ItAssetsTableDocument,
  UpdateItAssetDocument,
} from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import Form from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { sanitizeDateField } from 'modules/it_management/lib/dateUtils'
import { isValidPriceFormat } from 'modules/it_management/lib/validationUtils'

interface EditModalProps {
  asset: {
    id: string
    serialNumber?: string
    label?: string
    notes?: string
    status?: string
    purchaseDate?: string
    purchasePriceCents?: number
    warrantyEndDate?: string
    currency?: string
    itAssetModel?: {
      id: string
      name?: string
      brand?: string
      typeName?: string
    }
  }
  refreshAsset?: () => Promise<void>
  onClose?: () => void
}

const EditModal: React.FC<EditModalProps> = ({ asset, refreshAsset, onClose: externalOnClose }) => {
  const [formData, setFormData] = useState({
    serialNumber: asset.serialNumber || '',
    label: asset.label || '',
    notes: asset.notes || '',
    purchaseDate: asset.purchaseDate || '',
    purchasePrice: asset.purchasePriceCents ? (asset.purchasePriceCents / 100).toString() : '',
    warrantyEndDate: asset.warrantyEndDate || '',
    currency: asset.currency || 'EUR',
  })

  const {
    company: _company,
    access: { locale: _locale },
  } = useCurrentSession()
  const navigate = useNavigate()
  const { data: assetData, error: assetError } = useQuery(EditItAssetDocument, {
    variables: { assetId: asset.id },
    fetchPolicy: 'network-only',
  })

  const { data: currenciesData } = useQuery(GetCurrenciesDocument)
  const canEditAsset = true
  const [updateAsset, { loading }] = useMutation(UpdateItAssetDocument, {
    extractorFn: (mutationData) => mutationData.itManagementMutations.updateItmanagementItAsset,
    awaitRefetchQueries: true,
    refetchQueries: [ItAssetsTableDocument],
  })

  const onClose = () => {
    if (externalOnClose) {
      externalOnClose()
    } else {
      navigate(-1)
    }
  }

  if (assetData && (assetError || !canEditAsset)) {
    if (externalOnClose) {
      externalOnClose()
    } else {
      navigate(-1)
    }
    return null
  }

  if (!assetData || !canEditAsset) {
    return null
  }

  const handleSubmit = async () => {
    app.setProgress({
      message: i18n.t('it_management.assets.messages.updating_asset'),
    })

    const result = await updateAsset({
      variables: {
        id: asset.id,
        itAssetModelId: asset.itAssetModel?.id || '',
        serialNumber: formData.serialNumber || '',
        status: asset.status || 'in_stock',
        label: formData.label,
        notes: formData.notes,
        purchaseDate: sanitizeDateField(formData.purchaseDate),
        purchasePriceCents: formData.purchasePrice
          ? Math.round(parseFloat(formData.purchasePrice) * 100)
          : undefined,
        warrantyEndDate: sanitizeDateField(formData.warrantyEndDate),
        currency: formData.currency,
      },
    })

    app.setProgress(null)

    const handled = handleResult(result, (errors) => {
      let errorMessage = i18n.t('it_management.common.error_occurred')

      if (errors && typeof errors === 'object') {
        const errorMessages = Object.entries(errors).map(([field, message]) => {
          const fieldNames: Record<string, string> = {
            serialNumber: 'Serial Number',
            label: 'Item Code',
            purchaseDate: 'Purchase Date',
            warrantyEndDate: 'Warranty End Date',
            currency: 'Currency',
            purchasePriceCents: 'Purchase Price',
          }
          const fieldName = field === 'base' ? '' : `${fieldNames[field] ?? field}: `
          return `${fieldName}${message}`
        })

        if (errorMessages.length > 0) {
          errorMessage = errorMessages.join(', ')
        }
      }

      app.setFlash({
        type: 'wrong',
        message: errorMessage,
      })
    })

    if (!handled.success && handled.errors) {
      const graphqlErrors = handled.errors.filter(
        (graphqlError) =>
          graphqlError.__typename === 'SimpleError' &&
          graphqlError.message &&
          (graphqlError.message.includes('ISO8601Date') ||
            graphqlError.message.includes('coerce value') ||
            graphqlError.message.includes('invalid value'))
      )

      if (graphqlErrors.length > 0) {
        const dateFieldErrors = graphqlErrors.map((graphqlError) => {
          if (graphqlError.__typename === 'SimpleError') {
            if (graphqlError.message.includes('purchaseDate')) {
              return 'Purchase Date: Invalid date format'
            }
            if (graphqlError.message.includes('warrantyEndDate')) {
              return 'Warranty End Date: Invalid date format'
            }
            return graphqlError.message
          }
          return 'Invalid date format'
        })

        app.setFlash({
          type: 'wrong',
          message: dateFieldErrors.join(', '),
        })
      }
    }

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('it_management.assets.messages.asset_updated_successfully'),
      })

      if (refreshAsset) {
        await refreshAsset()
      }

      onClose()
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const currencyOptions = currenciesData?.apiCore.currenciesConnection.edges.map((edge) => ({
    value: edge.node.isoCode,
    label: `${edge.node.symbol} ${edge.node.isoCode}`,
  })) || [{ value: 'EUR', label: '€ EUR' }]

  const today = new Date()
  const purchaseDate = formData.purchaseDate ? new Date(formData.purchaseDate) : null
  const warrantyMinDate = purchaseDate || today

  return (
    <Modal
      title={i18n.t('it_management.assets.actions.edit_asset')}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
          <F0Button
            variant="outline"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault()
              e.stopPropagation()
              onClose()
            }}
            disabled={loading}
            label={i18n.t('common.cancel')}
          />
          <F0Button
            onClick={handleSubmit}
            loading={loading}
            label={i18n.t('it_management.assets.actions.save')}
          />
        </Box>
      }
    >
      <Form onSubmit={handleSubmit}>
        <FormRow>
          <div style={{ padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 8px 0' }}>{i18n.t('it_management.assets.presets.asset')}</h4>
            <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
              {asset.itAssetModel?.brand} {asset.itAssetModel?.name} ({asset.itAssetModel?.typeName}
              )
            </p>
          </div>
        </FormRow>

        <FormRow>
          <TextField
            label={i18n.t('it_management.common.serial_number')}
            placeholder={i18n.t('it_management.common.serial_number')}
            value={formData.serialNumber}
            onChange={(value) => handleInputChange('serialNumber', value)}
          />
        </FormRow>

        <FormRow>
          <TextField
            label={i18n.t('it_management.common.internal_label')}
            placeholder={i18n.t('it_management.new_equipment_modal.placeholders.internal_label')}
            description={i18n.t(
              'it_management.new_equipment_modal.fields.internal_label_description'
            )}
            value={formData.label}
            onChange={(value) => handleInputChange('label', value)}
          />
        </FormRow>

        <FormRow>
          <TextArea
            label={i18n.t('it_management.common.notes')}
            value={formData.notes}
            onChange={(value) => handleInputChange('notes', value)}
          />
        </FormRow>

        <FormRow>
          <DayPicker
            label={i18n.t('it_management.new_equipment_modal.fields.purchase_date')}
            placeholder={i18n.t('it_management.new_equipment_modal.placeholders.purchase_date')}
            value={formData.purchaseDate}
            onChange={(value) => handleInputChange('purchaseDate', value)}
            valueFormat="backend"
            format="ui"
            maxDate={today}
          />
        </FormRow>

        <FormRow>
          {/* Purchase Price and Currency */}
          <Box flexDirection="row" gap="s16">
            <TextField
              type="number"
              label={i18n.t('it_management.new_equipment_modal.fields.purchase_price')}
              placeholder={i18n.t('it_management.new_equipment_modal.placeholders.purchase_price')}
              value={formData.purchasePrice}
              min={0}
              onChange={(value) => {
                if (isValidPriceFormat(value)) {
                  handleInputChange('purchasePrice', value)
                }
              }}
            />
            <SelectField
              options={currencyOptions}
              missingLabel={i18n.t(
                'it_management.new_equipment_modal.placeholders.select_currency'
              )}
              label={i18n.t('it_management.common.currency')}
              value={formData.currency}
              onChange={(value: string) => handleInputChange('currency', value)}
            />
          </Box>
        </FormRow>

        <FormRow>
          <DayPicker
            label={i18n.t('it_management.new_equipment_modal.fields.warranty_end_date')}
            placeholder={i18n.t('it_management.new_equipment_modal.placeholders.warranty_end_date')}
            value={formData.warrantyEndDate}
            onChange={(value) => handleInputChange('warrantyEndDate', value)}
            valueFormat="backend"
            format="ui"
            minDate={warrantyMinDate}
          />
        </FormRow>
      </Form>
    </Modal>
  )
}

export default EditModal
